import React from 'react';
import {
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Popover,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

interface IPasswordRule {
    id: string;
    label: string;
    met: boolean;
}

interface IPasswordRequirementsPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    rules: IPasswordRule[];
}

// A passive popover that lists password rules and ticks each one green as the
// value satisfies it, updating live while the user types. Presentation only: the
// caller owns the anchor element, the open state (usually "the field is focused")
// and computes `rules` from the policy module — this component carries no policy.
//
// `pointerEvents: none` + the disabled focus traps keep it a hint: the password
// field keeps the keyboard focus and the popover never steals a click.
export function PasswordRequirementsPopover({
    open,
    anchorEl,
    rules,
}: IPasswordRequirementsPopoverProps) {
    return (
        <Popover
            open={open && Boolean(anchorEl)}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            disableAutoFocus
            disableEnforceFocus
            hideBackdrop
            sx={{ pointerEvents: 'none' }}
        >
            <List dense sx={{ py: 0.5, px: 1 }}>
                {rules.map((rule) => (
                    <ListItem key={rule.id} dense sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            {rule.met ? (
                                <CheckCircleIcon
                                    fontSize={'small'}
                                    color={'success'}
                                />
                            ) : (
                                <RadioButtonUncheckedIcon
                                    fontSize={'small'}
                                    color={'disabled'}
                                />
                            )}
                        </ListItemIcon>
                        <ListItemText
                            primary={rule.label}
                            primaryTypographyProps={{
                                variant: 'body2',
                                color: rule.met
                                    ? 'success.main'
                                    : 'text.secondary',
                            }}
                        />
                    </ListItem>
                ))}
            </List>
        </Popover>
    );
}
